import React, { useState } from 'react';
import { Button, Dropdown, Input, InputNumber, Modal, Popconfirm, Spin, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import type { CastAppearance, FilmCharacter, FilmRefAsset } from '../../types/film';
import { useBankVars } from './useBankVars';
import { avisChip, refAssetView } from './refAssetUtils';
import { crowdCanRegisterAvis, crowdGenState, crowdRegenConfirm } from './crowdCardUtils';
import PromptChipEditor from './PromptChipEditor'; // 261010 P9D #29
import { faceTextOf } from './promptSurfaceUtils';
import WfIconButton from './WfIconButton'; // UI-assettabs
import { IconChip } from './StatusIcon';
import './bank.css';
import './assetCards.css';

/**
 * Thẻ ĐÁM ĐÔNG (đợt 260922 E1-P2) — theo thẻ "Khán giả đấu trường" trong mockup đã duyệt.
 *
 * Đám đông không có MỘT khuôn mặt ⇒ không 7 nhãn, không bộ đồ, không "theo cảnh". Thứ cần có: mô tả NHÓM (ai, mặc gì —
 * lưu ở `casting`, cột mọi đường đọc chữ đã dùng), quy mô, và họ xuất hiện ở đâu trong kịch bản.
 *
 * 260922 E3-P6 — ẢNH NHÓM: một hàng vài người khác nhau cùng thế giới, nền trắng, 16:9 (`group_ref_url`, cột riêng — không phải "mặt"). Chip
 * AVIS như ảnh bộ đồ; ảnh CHƯA vào clip (Epic 5).
 */
export interface FilmCrowdCardProps {
  character: FilmCharacter;
  appearance?: CastAppearance;
  planMember?:boolean;
  busy?: boolean;
  onSave: (c: FilmCharacter, data: { name?: string; casting?: string; headcount?: number | null }) => Promise<void>;
  onDelete: (c: FilmCharacter) => void;
  // ── 260922 E3-P6: ảnh nhóm ──
  genBusy?: boolean;
  /** `promptOverride` (P9D #29 — ô Custom Prompt): có chữ ⇒ thay mẫu hệ thống. */
  onGenGroup?: (c: FilmCharacter, promptOverride?: string) => void;
  /** Dòng sổ asset chủ CROWD của hồ sơ NÀY. */
  refAssets?: FilmRefAsset[];
  onRegisterAvis?: (c: FilmCharacter) => void;
  onReconcileAvis?: (rowId: string, c: FilmCharacter) => void;
  /** UI-assettabs — khung bước Tài sản: thẻ kiểu bảng (ảnh · tên · meta biểu tượng · nút biểu tượng · ⋯). Không truyền ⇒ thẻ Bank cũ nguyên. */
  compact?: boolean;
}

type Field = 'name' | 'casting';

const FilmCrowdCard: React.FC<FilmCrowdCardProps> = ({ character: c, appearance,planMember, busy, onSave, onDelete, genBusy, onGenGroup, refAssets, onRegisterAvis, onReconcileAvis, compact }) => {
  const vars = useBankVars();
  const [editing, setEditing] = useState<Field | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState<Field | 'headcount' | null>(null);
  const [countOpen, setCountOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customText, setCustomText] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);

  const commit = async () => {
    const f = editing;
    if (!f) return;
    const v = draft.trim();
    setEditing(null);
    const cur = ((f === 'name' ? c.name : c.casting) || '').trim();
    if (v === cur || (f === 'name' && !v)) return;
    setSaving(f);
    try { await onSave(c, { [f]: v }); } finally { setSaving(null); }
  };
  const saveCount = async (n: number | null) => {
    setCountOpen(false);
    if ((n ?? null) === (c.headcount ?? null)) return;
    setSaving('headcount');
    try { await onSave(c, { headcount: n }); } finally { setSaving(null); }
  };

  const gen = crowdGenState(c);
  const avisView = c.groupRefUrl ? refAssetView((refAssets || []).filter((r) => r.ownerId === c.id && r.provider === 'AVIS'), c.groupRefUrl) : null;
  const avis = avisView ? avisChip(avisView) : null;
  const menu: MenuProps['items'] = [
    // 261010 P9D #29 — Custom Prompt cho ảnh nhóm (thay mẫu hệ thống). Cùng cổng với nút Gen: thiếu mô tả nhóm thì tắt.
    ...(onGenGroup && gen.canGen ? [{ key: 'custom', label: 'Custom Prompt ảnh nhóm…', onClick: () => { setCustomText(''); setCustomOpen(true); } }, { type: 'divider' as const }] : []),
    // Không khi `failed`: AVIS đã từ chối ảnh này thì thử lại vẫn bị, mỗi lần là một asset vĩnh viễn (backend cũng chặn).
    ...(onRegisterAvis && avisView && crowdCanRegisterAvis(avisView.state)
      ? [{ key: 'avis', label: avisView.state === 'stale' ? 'Đăng ký AVIS cho ảnh nhóm hiện tại' : 'Đăng ký AVIS (KYC — không xoá được)', onClick: () => onRegisterAvis(c) }, { type: 'divider' as const }]
      : []),
    {
    key: 'delete', danger: true, label: 'Xoá đám đông',
    onClick: () => Modal.confirm({
      title: `Xoá "${c.name}" khỏi Bank?`, content: 'Tên này sẽ lại hiện trong danh sách "chưa có hồ sơ".',
      okText: 'Xoá', okButtonProps: { danger: true }, cancelText: 'Huỷ', onOk: () => onDelete(c),
    }),
  }];
  const desc = (c.casting || '').trim();

  if (compact) {
    const avisTone = !avisView ? 'todo' : avisView.state === 'active' ? 'ok' : avisView.state === 'processing' ? 'doing' : avisView.state === 'stale' ? 'warn' : avisView.state === 'none' ? 'todo' : 'bad';
    const regen = gen.canGen ? (
      c.groupRefUrl ? (
        <Popconfirm title={`Tạo lại ảnh nhóm "${c.name}"?`} description={<div style={{ maxWidth: 300 }}>{crowdRegenConfirm(!!c.groupAvisAuto)}</div>}
          okText="Tạo lại" cancelText="Thôi" onConfirm={() => onGenGroup?.(c)}>
          <span className="wf-ib-w"><WfIconButton icon="reload" label="Tạo lại ảnh nhóm" loading={genBusy} testId="ac-gen" /></span>
        </Popconfirm>
      ) : <WfIconButton icon="picture" label="Tạo ảnh nhóm" loading={genBusy} onClick={() => onGenGroup?.(c)} testId="ac-gen" />
    ) : <WfIconButton icon="picture" label={c.groupRefUrl ? 'Tạo lại ảnh nhóm' : 'Tạo ảnh nhóm'} disabled why={gen.hint} testId="ac-gen" />;
    const events = appearance?.events ?? 0;
    return (
      <div className="ac-card" style={vars} data-testid="ac-crowd-card">
        <Modal open={customOpen} title={`Custom Prompt — ảnh nhóm "${c.name}"`} okText="Gen ảnh nhóm" cancelText="Đóng" destroyOnClose onCancel={() => setCustomOpen(false)}
          okButtonProps={{ disabled: !faceTextOf(customText), loading: genBusy }}
          onOk={() => { const t = faceTextOf(customText); if (!t || !onGenGroup) return; setCustomOpen(false); onGenGroup(c, t); }}>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>Prompt tự viết <b>thay hẳn</b> mẫu hệ thống (mẫu: một hàng người khác nhau, nền trắng, 16:9, đúng thế giới của phim). Muốn dùng mẫu thì đóng ô này và bấm tạo thường. Chip (nếu chèn) chỉ thành tên trong câu — ảnh nhóm gen từ chữ.</p>
          <PromptChipEditor inline value="" minHeight={140} onChange={setCustomText} />
        </Modal>
        <div className="bk-sheet">
          {c.groupRefUrl
            ? <img className="main" src={c.groupRefUrl} alt={c.name} style={{ objectFit: 'contain', background: '#fff' }} />
            : <div className="empty">Chưa có ảnh nhóm{onGenGroup ? regen : null}</div>}
          {genBusy && c.groupRefUrl && <div className="empty" style={{ background: 'rgba(0,0,0,.35)' }}><Spin /></div>}
        </div>
        <div className="ac-body">
          {editing === 'name'
            ? <Input autoFocus size="small" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onPressEnter={commit}
                onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }} />
            : <h3 className="ac-name" title={c.name} onClick={() => { setEditing('name'); setDraft(c.name); }}>{saving === 'name' ? <Spin size="small" /> : c.name}</h3>}
          <div className="ac-sub">
            <span className="ac-tag">ĐÁM ĐÔNG</span>
            {countOpen
              ? <InputNumber size="small" autoFocus min={1} max={999} defaultValue={c.headcount ?? undefined} style={{ width: 80 }}
                  onBlur={(e) => { const n = Number((e.target as HTMLInputElement).value); saveCount(Number.isFinite(n) && n > 0 ? n : null); }}
                  onPressEnter={(e) => { const n = Number((e.target as HTMLInputElement).value); saveCount(Number.isFinite(n) && n > 0 ? n : null); }} />
              : <span className="t" onClick={() => setCountOpen(true)}>{saving === 'headcount' ? <Spin size="small" /> : (c.headcount ? `~${c.headcount} người` : '＋ quy mô')}</span>}
          </div>
          <div className="ac-meta">
            {c.groupRefUrl && avis && avisView ? <IconChip label={`AVIS — ${avisView.state === 'none' && !c.groupAvisAuto ? 'Chưa đăng ký AVIS — đăng ký tay ở menu ⋯ khi ảnh đã đạt.' : avis.title}`} icon="safety" tone={avisTone} /> : null}
            {c.groupRefUrl ? <IconChip label="Ảnh nhóm chưa được gửi vào clip — Epic 5 (gen clip qua AVIS)." icon="video" tone="todo" /> : null}
            <IconChip label={c.groupRefUrl ? 'Có ảnh nhóm' : 'Chưa có ảnh nhóm'} icon="picture" tone={c.groupRefUrl ? 'ok' : 'warn'} />
            {appearance ? <IconChip label={`${events} video/event nhắc tới nhóm này`} icon="list" tone="meta" value={String(events)} /> : null}
          </div>
          <div className="ac-sub" style={{ alignItems: 'flex-start' }}>
            {editing === 'casting'
              ? <Input.TextArea autoFocus size="small" autoSize={{ minRows: 2, maxRows: 6 }} value={draft}
                  placeholder="Họ là ai, mặc gì, đông cỡ nào — vd: dân La Mã áo tunic sáng màu ngồi kín khán đài"
                  onChange={(e) => setDraft(e.target.value)} onBlur={commit}
                  onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); } }} />
              : <div className={`ac-desc clamp${desc ? '' : ' miss'}`} style={{ margin: 0 }} title={desc || undefined} onClick={() => { setEditing('casting'); setDraft(desc); }}>
                  {saving === 'casting' ? <Spin size="small" /> : (desc || 'Chưa có mô tả — bấm để thêm (họ là ai, mặc gì)')}
                </div>}
          </div>
          {!gen.canGen ? <div className="ac-note">{gen.hint}</div> : null}
        </div>
        {detailOpen ? (
          <div className="ac-detail" style={{ padding: '8px 12px' }}>
            {c.aliases?.length > 0 && (
              <>
                <div className="bk-lbl">Kịch bản cũng gọi là</div>
                <div className="bk-chips">{c.aliases.map((a) => <span key={a} className="bk-chip">{a}</span>)}</div>
              </>
            )}
            <div className="bk-lbl" style={{ marginTop: 6 }}>Xuất hiện trong kịch bản {appearance ? <span className="bk-cnt">{appearance.events} event</span> : null}</div>
            {appearance?.samples?.length
              ? <div className="bk-appear">{appearance.samples.map((s) => (
                  <div key={s.eventId}><b>{s.sceneOrder ? `S${s.sceneOrder}` : '—'}</b> · {s.text || '(event chưa có mô tả)'}</div>
                ))}</div>
              : <div className="bk-dimline" style={{ marginTop: 0 }}>{planMember?'Dùng trong tập · chưa gán video':'Plan đang mở không có event nào nhắc tới nhóm này.'}</div>}
            {avis && avisView && avisView.state === 'unknown' && avisView.rowId && onReconcileAvis ? (
              <button type="button" className={`bk-chip ${avis.cls}`} title={avis.title} style={{ marginTop: 6 }} onClick={() => onReconcileAvis(avisView.rowId!, c)}>{avis.text} — đối soát</button>
            ) : null}
          </div>
        ) : null}
        <div className="ac-acts">
          {onGenGroup ? regen : null}
          <WfIconButton icon="edit" label={detailOpen ? 'Thu gọn' : 'Sửa / Chi tiết'} pressed={detailOpen} onClick={() => setDetailOpen((v) => !v)} testId="ac-detail-toggle" />
          <span className="sp" />
          <Dropdown menu={{ items: menu }} trigger={['click']}>
            <span className="wf-ib-w"><WfIconButton icon="ellipsis" label="Thêm" loading={busy} testId="ac-more" /></span>
          </Dropdown>
        </div>
      </div>
    );
  }

  return (
    <div className="bk-card" style={vars}>
      <Modal open={customOpen} title={`Custom Prompt — ảnh nhóm "${c.name}"`} okText="Gen ảnh nhóm" cancelText="Đóng" destroyOnClose onCancel={() => setCustomOpen(false)}
        okButtonProps={{ disabled: !faceTextOf(customText), loading: genBusy }}
        onOk={() => { const t = faceTextOf(customText); if (!t || !onGenGroup) return; setCustomOpen(false); onGenGroup(c, t); }}>
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>Prompt tự viết <b>thay hẳn</b> mẫu hệ thống (mẫu: một hàng người khác nhau, nền trắng, 16:9, đúng thế giới của phim). Muốn dùng mẫu thì đóng ô này và bấm Gen thường. Chip (nếu chèn) chỉ thành tên trong câu — ảnh nhóm gen từ chữ.</p>
        <PromptChipEditor inline value="" minHeight={140} onChange={setCustomText} />
      </Modal>
      <div className="bk-sheet">
        {c.groupRefUrl
          ? <img className="main" src={c.groupRefUrl} alt={c.name} style={{ objectFit: 'contain', background: '#fff' }} />
          : (
            <div className="empty">
              Chưa có ảnh nhóm
              {onGenGroup && (
                <Tooltip title={gen.canGen ? gen.hint : ''}>
                  <Button size="small" type="primary" disabled={!gen.canGen} loading={genBusy} onClick={() => onGenGroup(c)}>{gen.label}</Button>
                </Tooltip>
              )}
            </div>
          )}
        {genBusy && c.groupRefUrl && <div className="empty" style={{ background: 'rgba(0,0,0,.35)' }}><Spin /></div>}
      </div>
      {!gen.canGen && <div className="bk-note" style={{ borderLeftColor: 'var(--bk-warn)' }}>{gen.hint}</div>}
      {c.groupRefUrl && (
        <div className="bk-status">
          {avis && avisView && (avisView.state === 'unknown' && avisView.rowId && onReconcileAvis
            ? <button type="button" className={`bk-chip ${avis.cls}`} title={avis.title} onClick={() => onReconcileAvis(avisView.rowId!, c)}>{avis.text}</button>
            : <span className={`bk-chip ${avis.cls}`} title={avisView.state === 'none' && !c.groupAvisAuto ? 'Chưa đăng ký AVIS — đăng ký tay ở menu ⋯ khi ảnh đã đạt.' : avis.title}>{avis.text}</span>)}
          <span className="bk-chip" title="Ảnh nhóm chưa được gửi vào clip — Epic 5 (gen clip qua AVIS).">Chưa vào clip</span>
          <span style={{ flex: 1 }} />
          {onGenGroup && (
            <Popconfirm title={`Gen lại ảnh nhóm "${c.name}"?`} description={<div style={{ maxWidth: 300 }}>{crowdRegenConfirm(!!c.groupAvisAuto)}</div>}
              okText="Gen lại" cancelText="Thôi" disabled={!gen.canGen} onConfirm={() => onGenGroup(c)}>
              <Button size="small" disabled={!gen.canGen} loading={genBusy}>{gen.label}</Button>
            </Popconfirm>
          )}
        </div>
      )}
      <div className="bk-body">
        <div className="bk-name">
          {editing === 'name'
            ? <Input autoFocus size="small" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onPressEnter={commit}
                onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }} />
            : <h3 onClick={() => { setEditing('name'); setDraft(c.name); }}>{saving === 'name' ? <Spin size="small" /> : c.name}</h3>}
          <span className="bk-kind">ĐÁM ĐÔNG</span>
          {countOpen
            ? <InputNumber size="small" autoFocus min={1} max={999} defaultValue={c.headcount ?? undefined} style={{ width: 80 }}
                onBlur={(e) => { const n = Number((e.target as HTMLInputElement).value); saveCount(Number.isFinite(n) && n > 0 ? n : null); }}
                onPressEnter={(e) => { const n = Number((e.target as HTMLInputElement).value); saveCount(Number.isFinite(n) && n > 0 ? n : null); }} />
            : <span className="bk-role" onClick={() => setCountOpen(true)}>{saving === 'headcount' ? <Spin size="small" /> : (c.headcount ? `~${c.headcount} người` : '＋ quy mô')}</span>}
          <Dropdown menu={{ items: menu }} trigger={['click']}>
            <Button className="more" size="small" type="text" icon={<MoreOutlined />} loading={busy} />
          </Dropdown>
        </div>

        <div className="bk-lbl">Mô tả nhóm</div>
        {editing === 'casting'
          ? <Input.TextArea autoFocus size="small" autoSize={{ minRows: 2, maxRows: 6 }} value={draft}
              placeholder="Họ là ai, mặc gì, đông cỡ nào — vd: dân La Mã áo tunic sáng màu ngồi kín khán đài"
              onChange={(e) => setDraft(e.target.value)} onBlur={commit}
              onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); } }} />
          : <div className={`bk-desc ${desc ? '' : 'miss'}`} onClick={() => { setEditing('casting'); setDraft(desc); }}>
              {saving === 'casting' ? <Spin size="small" /> : (desc || 'Chưa có mô tả — bấm để thêm (họ là ai, mặc gì)')}
            </div>}

        {c.aliases?.length > 0 && (
          <>
            <div className="bk-lbl">Kịch bản cũng gọi là</div>
            <div className="bk-chips">{c.aliases.map((a) => <span key={a} className="bk-chip">{a}</span>)}</div>
          </>
        )}

        <div className="bk-lbl">Xuất hiện trong kịch bản {appearance ? <span className="bk-cnt">{appearance.events} event</span> : null}</div>
        {appearance?.samples?.length
          ? <div className="bk-appear">{appearance.samples.map((s) => (
              <div key={s.eventId}><b>{s.sceneOrder ? `S${s.sceneOrder}` : '—'}</b> · {s.text || '(event chưa có mô tả)'}</div>
            ))}</div>
          : <div className="bk-dimline" style={{ marginTop: 0 }}>{planMember?'Dùng trong tập · chưa gán video':'Plan đang mở không có event nào nhắc tới nhóm này.'}</div>}
      </div>
    </div>
  );
};

export default FilmCrowdCard;
